// src/components/admin/GestionCarpetas.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner, Row, Col, Badge } from 'react-bootstrap';
import DatosMaestrosService from '../../services/datosMaestrosService';
import ModalConfirmacionCambioEstado from '../ModalConfirmacionCambioEstado';
import MenuAccionesFila from './MenuAccionesFila';
import PanelFiltros, { CampoFiltro } from '../PanelFiltros';
import '../../style/components/GestionDatosMaestros.css';
import useBreakpoint from '../../hooks/useBreakpoint';
import BottomSheet from '../mobile/BottomSheet';
import BotonFiltros from '../mobile/BotonFiltros';
import ChipsFiltros from '../mobile/ChipsFiltros';
import { coincideBusqueda } from '../../utils/textoComparable';

// Catálogo de las carpetas físicas del archivo del estudio: dónde está guardado el papel de cada
// trámite. Antes era texto libre en la ficha del trámite, así que la misma carpeta se escribía de
// N formas y no había manera de saber qué contenía cada una.
//
// El orden de la tabla refleja la disposición física del archivo y se cambia con las flechas.
const FORM_VACIO = {
    nombre: '',
    ubicacion: '',
    descripcion: ''
};

const GestionCarpetas = () => {
    const [carpetas, setCarpetas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reordenando, setReordenando] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedCarpeta, setSelectedCarpeta] = useState(null);
    const [formData, setFormData] = useState(FORM_VACIO);
    const [alert, setAlert] = useState({ show: false, message: '', variant: '' });

    // Modal de confirmación de eliminación
    const [showModalEliminar, setShowModalEliminar] = useState(false);
    const [carpetaAEliminar, setCarpetaAEliminar] = useState(null);
    const [eliminando, setEliminando] = useState(false);

    // Filtros
    const [filterNombre, setFilterNombre] = useState('');
    const [filterEstado, setFilterEstado] = useState('TODAS');
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    const mostrarAlerta = (message, variant) => {
        setAlert({ show: true, message, variant });
        setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 4000);
    };

    const cargarCarpetas = useCallback(async () => {
        setLoading(true);
        try {
            // false: el ABM también lista las inactivas para poder reactivarlas.
            const response = await DatosMaestrosService.getCarpetas(false);
            setCarpetas(response.data);
        } catch (error) {
            mostrarAlerta('Error al cargar las carpetas', 'danger');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        cargarCarpetas();
    }, [cargarCarpetas]);

    const handleOpenModal = (mode, carpeta = null) => {
        setModalMode(mode);
        if (mode === 'edit' && carpeta) {
            setSelectedCarpeta(carpeta);
            setFormData({
                nombre: carpeta.nombre || '',
                ubicacion: carpeta.ubicacion || '',
                descripcion: carpeta.descripcion || ''
            });
        } else {
            setSelectedCarpeta(null);
            setFormData(FORM_VACIO);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedCarpeta(null);
        setFormData(FORM_VACIO);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.nombre.trim()) {
            mostrarAlerta('El nombre de la carpeta es obligatorio.', 'danger');
            return;
        }

        setLoading(true);
        try {
            if (modalMode === 'create') {
                await DatosMaestrosService.createCarpeta(formData);
                mostrarAlerta('✅ Carpeta creada exitosamente', 'success');
            } else {
                await DatosMaestrosService.updateCarpeta(selectedCarpeta.id, formData);
                mostrarAlerta('✅ Carpeta actualizada exitosamente', 'success');
            }
            handleCloseModal();
            await cargarCarpetas();
        } catch (error) {
            mostrarAlerta(error?.response?.data?.message || '❌ Error al guardar la carpeta', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // Activar / desactivar. Una carpeta desactivada deja de ofrecerse al asignar, pero los
    // trámites que ya estaban en ella la siguen mostrando.
    const handleCambiarEstado = async (carpeta) => {
        try {
            await DatosMaestrosService.updateCarpeta(carpeta.id, { activo: !carpeta.activo });
            await cargarCarpetas();
        } catch (error) {
            mostrarAlerta(error?.response?.data?.message || '❌ No se pudo cambiar el estado', 'danger');
        }
    };

    // Mueve la carpeta una posición y manda el catálogo entero en el orden nuevo. Se trabaja
    // sobre la lista completa (no la filtrada) para que el orden guardado no dependa de lo que
    // haya escrito el usuario en los filtros.
    const moverCarpeta = async (id, direccion) => {
        const ordenadas = [...carpetas];
        const desde = ordenadas.findIndex(c => c.id === id);
        const hasta = desde + direccion;
        if (desde < 0 || hasta < 0 || hasta >= ordenadas.length) return;

        const [movida] = ordenadas.splice(desde, 1);
        ordenadas.splice(hasta, 0, movida);
        setCarpetas(ordenadas); // feedback inmediato; la respuesta del server manda igual

        setReordenando(true);
        try {
            const response = await DatosMaestrosService.reordenarCarpetas(ordenadas.map(c => c.id));
            setCarpetas(response.data);
        } catch (error) {
            mostrarAlerta(error?.response?.data?.message || '❌ No se pudo reordenar', 'danger');
            await cargarCarpetas();
        } finally {
            setReordenando(false);
        }
    };

    const handleDelete = (id) => {
        setCarpetaAEliminar(carpetas.find(c => c.id === id));
        setShowModalEliminar(true);
    };

    const confirmarEliminar = async () => {
        if (!carpetaAEliminar) return;

        setEliminando(true);
        try {
            await DatosMaestrosService.deleteCarpeta(carpetaAEliminar.id);
            mostrarAlerta('✅ Carpeta eliminada exitosamente', 'success');
            setShowModalEliminar(false);
            setCarpetaAEliminar(null);
            await cargarCarpetas();
        } catch (error) {
            // El backend explica por qué no se puede (carpeta en uso) y sugiere desactivarla.
            mostrarAlerta(
                error?.response?.data?.message || '❌ Error al eliminar la carpeta',
                'danger'
            );
            setShowModalEliminar(false);
            setCarpetaAEliminar(null);
        } finally {
            setEliminando(false);
        }
    };

    const cancelarEliminar = () => {
        setShowModalEliminar(false);
        setCarpetaAEliminar(null);
    };

    const getCarpetasFiltradas = () => {
        let filtradas = [...carpetas];

        if (filterNombre.trim()) {
            filtradas = filtradas.filter(c => coincideBusqueda([c.nombre, c.ubicacion], filterNombre));
        }

        if (filterEstado === 'ACTIVAS') {
            filtradas = filtradas.filter(c => c.activo);
        } else if (filterEstado === 'INACTIVAS') {
            filtradas = filtradas.filter(c => !c.activo);
        }

        return filtradas;
    };

    // Las flechas mueven dentro del catálogo completo, así que solo tienen sentido cuando se está
    // viendo la lista entera: con filtros aplicados el usuario vería saltos que no puede predecir.
    const hayFiltros = filterNombre.trim() !== '' || filterEstado !== 'TODAS';
    const carpetasFiltradas = getCarpetasFiltradas();

    const mensajeEliminar = carpetaAEliminar
        ? (carpetaAEliminar.cantidadTramites > 0
            ? `"${carpetaAEliminar.nombre}" tiene ${carpetaAEliminar.cantidadTramites} trámite(s) guardados. No se puede eliminar: desactivala para que deje de ofrecerse sin perder el histórico.`
            : `¿Confirma que desea eliminar la carpeta "${carpetaAEliminar.nombre}"?`)
        : '';

    return (
        <div className="gestion-datos-maestros">
            {alert.show && (
                <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: '', variant: '' })}>
                    {alert.message}
                </Alert>
            )}

            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <p className="text-muted small mb-0">
                    El orden de la lista refleja cómo están dispuestas en el estudio. Las inactivas no se
                    ofrecen al asignar una carpeta.
                </p>
                <div className="d-flex flex-wrap gap-2 ms-auto">
                    <Button
                        variant="outline-secondary"
                        onClick={cargarCarpetas}
                        disabled={loading}
                        className="shadow-sm"
                        title="Recargar lista"
                    >
                        <i className={`bi bi-arrow-clockwise ${loading ? 'spin' : ''}`}></i>
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => handleOpenModal('create')}
                        className="shadow-sm"
                    >
                        <i className="bi bi-plus-circle me-2"></i>
                        Nueva Carpeta
                    </Button>
                </div>
            </div>

            {/* Filtros */}
            {(() => {
                const limpiarFiltros = () => { setFilterNombre(''); setFilterEstado('TODAS'); };
                const ETIQUETAS_ESTADO = { ACTIVAS: 'Solo activas', INACTIVAS: 'Solo inactivas' };
                const chipsFiltros = [
                    filterNombre.trim() !== '' && { key: 'nombre', label: filterNombre, onQuitar: () => setFilterNombre('') },
                    filterEstado !== 'TODAS' && { key: 'estado', label: ETIQUETAS_ESTADO[filterEstado], onQuitar: () => setFilterEstado('TODAS') },
                ].filter(Boolean);
                const resumen = `Mostrando ${carpetasFiltradas.length} de ${carpetas.length} carpetas`
                    + (hayFiltros ? ' · quitá los filtros para poder reordenar' : '');

                const campoBuscar = (
                    <CampoFiltro etiqueta="Buscar" icono="bi-search" htmlFor="filtro-carpeta-nombre" ancho>
                        <Form.Control
                            id="filtro-carpeta-nombre"
                            className="panel-filtros-input"
                            type="text"
                            placeholder="Nombre o ubicación (ej: Liquidar 5B, Estante 3)…"
                            value={filterNombre}
                            onChange={(e) => setFilterNombre(e.target.value)}
                        />
                    </CampoFiltro>
                );
                const campoEstado = (
                    <CampoFiltro etiqueta="Estado" icono="bi-toggle-on" htmlFor="filtro-carpeta-estado">
                        <Form.Select
                            id="filtro-carpeta-estado"
                            className="panel-filtros-input"
                            value={filterEstado}
                            onChange={(e) => setFilterEstado(e.target.value)}
                        >
                            <option value="TODAS">Todas</option>
                            <option value="ACTIVAS">Solo activas</option>
                            <option value="INACTIVAS">Solo inactivas</option>
                        </Form.Select>
                    </CampoFiltro>
                );

                return esMobile ? (
                    <div className="mb-3">
                        <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                        <ChipsFiltros chips={chipsFiltros} />
                        {sheetFiltrosAbierto && (
                            <BottomSheet
                                titulo="Filtros"
                                onCerrar={() => setSheetFiltrosAbierto(false)}
                                accionPrimaria={chipsFiltros.length > 0 ? { label: 'Limpiar filtros', onClick: limpiarFiltros } : undefined}
                            >
                                {campoBuscar}
                                {campoEstado}
                            </BottomSheet>
                        )}
                        <small className="d-block mt-2 text-muted">{resumen}</small>
                    </div>
                ) : (
                    <PanelFiltros hayFiltros={chipsFiltros.length > 0} onLimpiar={limpiarFiltros} resumen={resumen}>
                        {campoBuscar}
                        {campoEstado}
                    </PanelFiltros>
                );
            })()}

            {loading && carpetas.length === 0 ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div className="table-responsive">
                    <Table hover className="datos-maestros-table">
                        <thead>
                            <tr>
                                <th className="text-center" style={{ width: 90 }}>Orden</th>
                                <th>Nombre</th>
                                <th>Ubicación</th>
                                <th>Descripción</th>
                                <th className="text-center">Trámites</th>
                                <th className="text-center">Estado</th>
                                <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                            </tr>
                        </thead>
                        <tbody>
                            {carpetasFiltradas.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center text-muted py-4">
                                        {carpetas.length === 0
                                            ? 'No hay carpetas registradas'
                                            : 'No se encontraron carpetas con los filtros aplicados'}
                                    </td>
                                </tr>
                            ) : (
                                carpetasFiltradas.map((carpeta) => {
                                    const posicion = carpetas.findIndex(c => c.id === carpeta.id);
                                    return (
                                        <tr key={carpeta.id} className={carpeta.activo ? '' : 'text-muted'}>
                                            <td className="text-center">
                                                <div className="d-flex gap-1 justify-content-center">
                                                    <Button
                                                        size="sm"
                                                        variant="link"
                                                        className="btn-icono-neutro"
                                                        aria-label="Subir"
                                                        onClick={() => moverCarpeta(carpeta.id, -1)}
                                                        disabled={hayFiltros || reordenando || posicion === 0}
                                                        title={hayFiltros ? 'Quitá los filtros para reordenar' : 'Subir'}
                                                    >
                                                        <i className="bi bi-arrow-up"></i>
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="link"
                                                        className="btn-icono-neutro"
                                                        aria-label="Bajar"
                                                        onClick={() => moverCarpeta(carpeta.id, 1)}
                                                        disabled={hayFiltros || reordenando || posicion === carpetas.length - 1}
                                                        title={hayFiltros ? 'Quitá los filtros para reordenar' : 'Bajar'}
                                                    >
                                                        <i className="bi bi-arrow-down"></i>
                                                    </Button>
                                                </div>
                                            </td>
                                            <td className="fw-semibold">
                                                <i className="bi bi-folder-fill me-2" style={{ color: '#0d9488' }}></i>
                                                {carpeta.nombre}
                                            </td>
                                            <td>{carpeta.ubicacion || <span className="text-muted">—</span>}</td>
                                            <td>{carpeta.descripcion || <span className="text-muted">—</span>}</td>
                                            <td className="text-center">
                                                <Badge bg={carpeta.cantidadTramites > 0 ? 'info' : 'light'}
                                                       text={carpeta.cantidadTramites > 0 ? undefined : 'dark'}>
                                                    {carpeta.cantidadTramites ?? 0}
                                                </Badge>
                                            </td>
                                            <td className="text-center">
                                                <Form.Check
                                                    type="switch"
                                                    id={`carpeta-activa-${carpeta.id}`}
                                                    checked={!!carpeta.activo}
                                                    onChange={() => handleCambiarEstado(carpeta)}
                                                    title={carpeta.activo ? 'Activa' : 'Inactiva'}
                                                />
                                            </td>
                                            <td className="text-end">
                                                <MenuAccionesFila acciones={[
                                                    { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => handleOpenModal('edit', carpeta) },
                                                    { etiqueta: 'Eliminar', icono: 'bi-trash', peligro: true, onClick: () => handleDelete(carpeta.id) },
                                                ]} />
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* Alta / edición */}
            <Modal show={showModal} onHide={handleCloseModal} centered scrollable>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>
                        <i className={`bi ${modalMode === 'create' ? 'bi-plus-circle' : 'bi-pencil-square'} me-2 text-primary`}></i>
                        {modalMode === 'create' ? 'Nueva Carpeta' : 'Editar Carpeta'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body className="p-4">
                        <Row className="g-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">
                                        Nombre <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ej: Liquidar 5B"
                                        value={formData.nombre}
                                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                        required
                                        autoFocus
                                    />
                                    <Form.Text className="text-muted">
                                        Como la nombra el estudio; es lo que se ve en la ficha del trámite
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">Ubicación física</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Ej: Estante 5, fila B"
                                        value={formData.ubicacion}
                                        onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })}
                                    />
                                    <Form.Text className="text-muted">
                                        Dónde está en el estudio. Se puede cambiar si la carpeta se muda de lugar
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label className="fw-semibold">Descripción</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        placeholder="Notas internas sobre qué se guarda acá (opcional)"
                                        value={formData.descripcion}
                                        onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="bg-light">
                        <Button variant="outline-secondary" onClick={handleCloseModal} disabled={loading}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading && <Spinner as="span" animation="border" size="sm" className="me-2" />}
                            Guardar
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <ModalConfirmacionCambioEstado
                isOpen={showModalEliminar}
                onClose={cancelarEliminar}
                onConfirm={confirmarEliminar}
                titulo="Eliminar Carpeta"
                mensaje={mensajeEliminar}
                cargando={eliminando}
                textoBotonConfirmar="Eliminar"
                variantBotonConfirmar="danger"
            />
        </div>
    );
};

export default GestionCarpetas;
